import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { extractErrorMessage } from '../../core/services/api';
import {
  llmAgent,
  resources,
  type LlmJobState,
  type LlmJobStatus,
} from '../../core/services/resources';
import type { TenantBrand, TenantOrganization } from '../../core/types/data-models';
import { useTenant } from '../../core/contexts/TenantContext';

/**
 * Shape of `result` returned by `generateCompetitorsAction`. Mirrors
 * the "Output format" section of the prompt template — the model
 * returns organizations as the top-level groups, each carrying a
 * `brands` array. When `organization_id` or `brand_id` is non-null it
 * means the row already exists in the tenant catalog (the prompt was
 * given the catalog with ids) and we should reuse it on save instead
 * of creating a duplicate. `associationType` ranges over the four
 * `LineOfBizBrand` association types, so the dialog displays (and
 * persists) OWNER suggestions alongside COMPETITOR ones.
 */
type BrandAssociationType = 'OWNER' | 'COMPETITOR' | 'SUBSIDIARY' | 'PARTNER';

interface CompetitorOrgGroup {
  organization_id: number | null;
  organization: string;
  organizationDescription?: string;
  organizationWebSiteUrl?: string;
  organizationLogoUrl?: string;
  brands: Array<{
    brand_id: number | null;
    brand: string;
    /** Name of this brand's parent brand within the same org, or null. */
    parentBrandName?: string | null;
    /** true for focus-level competitors; false for ancestor brands. */
    isFocusLevel?: boolean;
    brandDescription?: string;
    brandWebSiteUrl?: string;
    brandLogoUrl?: string;
    associationType: BrandAssociationType;
    associationDescription?: string;
  }>;
}

interface CompetitorsResult {
  /** The brand level the model focused the competitors on. */
  requested_brand_level?: string;
  competitor_brands?: CompetitorOrgGroup[];
}

/**
 * Flat row used by the UI — one card per suggested brand, with the
 * parent organization context attached. Pre-computed from the nested
 * model output so render + selection state can stay 1-D.
 */
interface FlatPick {
  organization_id: number | null;
  organization: string;
  organizationDescription?: string;
  organizationWebSiteUrl?: string;
  organizationLogoUrl?: string;
  brand_id: number | null;
  brand: string;
  /** Parent brand name within the same org (hierarchy), or null. */
  parentBrandName: string | null;
  /** true for focus-level competitors, false for ancestor brands. */
  isFocusLevel: boolean;
  /** Depth in the org's brand hierarchy (0 = directly under the org). */
  depth: number;
  brandDescription?: string;
  brandWebSiteUrl?: string;
  brandLogoUrl?: string;
  associationType: BrandAssociationType;
  associationDescription?: string;
}

const norm = (s: string | null | undefined): string =>
  (s ?? '').trim().toLowerCase();

/**
 * Flatten the model's nested orgs → brands shape into one row per brand,
 * computing each brand's depth within its organization by walking the
 * `parentBrandName` chain. Rows are emitted parent-before-child and
 * sorted by depth so the hierarchy renders top-down with indentation.
 */
function flattenSuggestions(result: CompetitorsResult | undefined): FlatPick[] {
  const out: FlatPick[] = [];
  for (const org of result?.competitor_brands ?? []) {
    const brands = org.brands ?? [];
    // Name → brand within this org, for parent-chain depth resolution.
    const byName = new Map<string, (typeof brands)[number]>();
    for (const b of brands) byName.set(norm(b.brand), b);

    const depthOf = (b: (typeof brands)[number]): number => {
      let depth = 0;
      const seen = new Set<string>([norm(b.brand)]);
      let parentName = b.parentBrandName ?? null;
      while (parentName) {
        const key = norm(parentName);
        if (seen.has(key)) break; // cycle guard
        seen.add(key);
        depth += 1;
        const parent = byName.get(key);
        parentName = parent?.parentBrandName ?? null;
        if (depth > 10) break; // runaway guard
      }
      return depth;
    };

    const rows = brands.map((b) => ({
      organization_id: org.organization_id ?? null,
      organization: org.organization,
      organizationDescription: org.organizationDescription,
      organizationWebSiteUrl: org.organizationWebSiteUrl,
      organizationLogoUrl: org.organizationLogoUrl,
      brand_id: b.brand_id ?? null,
      brand: b.brand,
      parentBrandName: b.parentBrandName ?? null,
      // Default true when the model omits it — a flat (single-level)
      // response then behaves exactly as before.
      isFocusLevel: b.isFocusLevel ?? true,
      depth: depthOf(b),
      brandDescription: b.brandDescription,
      brandWebSiteUrl: b.brandWebSiteUrl,
      brandLogoUrl: b.brandLogoUrl,
      associationType: b.associationType,
      associationDescription: b.associationDescription,
    }));
    // Stable sort by depth so parents precede their children in the list.
    rows.sort((a, z) => a.depth - z.depth);
    out.push(...rows);
  }
  return out;
}

interface GenerateCompetitorsDialogProps {
  open: boolean;
  lineOfBizId: number | null;
  onClose: () => void;
}

const TERMINAL_STATES: ReadonlySet<LlmJobState> = new Set([
  'completed',
  'failed',
  'cancelled',
]);

/**
 * Dialog that drives the generate-competitors flow:
 *
 *   1. POST /lines-of-business/:id/brands/generate-competitors to start a job.
 *   2. Poll GET /llm-agent/jobs/:jobId every 1.5s until terminal.
 *   3. Render the result with one checkbox per suggested competitor; the
 *      user picks which competitors to save and clicks Save.
 *   4. For each selected row: upsert the Organization (by exact name),
 *      upsert the Brand (by name within the org), then PUT the
 *      LineOfBizBrand junction with associationType=COMPETITOR.
 *
 * Unlike the industries flow (which saves Markdown into a single LOB
 * column) this dialog has to materialize real Brand/Organization rows
 * before it can attach the junction — hence the find-or-create dance
 * on save. Name lookup is case-insensitive and trimmed.
 */
export function GenerateCompetitorsDialog({
  open,
  lineOfBizId,
  onClose,
}: GenerateCompetitorsDialogProps) {
  const queryClient = useQueryClient();
  const { activeTenantId } = useTenant();

  // Job lifecycle state — reset on every open. We keep the latest status
  // in a single object so the polling effect re-renders on each tick
  // without needing one state slot per field.
  const [status, setStatus] = useState<LlmJobStatus<CompetitorsResult> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Latest jobId — kept in a ref so the polling interval can read it
  // without re-subscribing every tick.
  const jobIdRef = useRef<string | null>(null);

  // ----- start job ----------------------------------------------------
  const start = useMutation({
    mutationFn: (id: number) => resources.generateLineOfBizCompetitors(id),
    onSuccess: (data) => {
      jobIdRef.current = data.jobId;
      setStatus(data.status as LlmJobStatus<CompetitorsResult>);
    },
    onError: (err) => setStartError(extractErrorMessage(err)),
  });

  // ----- save selection ----------------------------------------------
  // Each pick turns into up-to-three round-trips:
  // (1) resolve organization — reuse `organization_id` from the model
  //     when present, otherwise find-or-create by name in the tenant.
  // (2) resolve brand — same pattern for `brand_id`.
  // (3) PUT the LineOfBizBrand junction with the picked
  //     `associationType` (OWNER / COMPETITOR / SUBSIDIARY / PARTNER).
  // We run sequentially so partial failures are obvious in the network
  // panel and we can surface the first failing message.
  const save = useMutation({
    mutationFn: async ({ chosen, all }: { chosen: FlatPick[]; all: FlatPick[] }) => {
      if (lineOfBizId == null || activeTenantId == null) return;
      const tenantId = activeTenantId;

      // Index ALL suggested picks (not just the selected ones) per org so
      // a selected child can resolve its ancestor brands even when the
      // user didn't tick them — the parent chain has to exist as
      // TenantBrand rows before a child can carry `parentBrandId`.
      const orgKeyOf = (p: FlatPick) =>
        p.organization_id != null ? `id:${p.organization_id}` : `name:${norm(p.organization)}`;
      const pickByOrgBrand = new Map<string, FlatPick>();
      for (const p of all) pickByOrgBrand.set(`${orgKeyOf(p)}|${norm(p.brand)}`, p);

      const orgIdCache = new Map<string, number>();
      const brandIdCache = new Map<FlatPick, number>();

      const resolveOrg = async (p: FlatPick): Promise<number> => {
        const key = orgKeyOf(p);
        const cached = orgIdCache.get(key);
        if (cached != null) return cached;
        const id =
          p.organization_id
          ?? (await findOrCreateTenantOrganization((p.organization || p.brand).trim(), tenantId, {
            description: p.organizationDescription,
            website: p.organizationWebSiteUrl,
            logoUrl: p.organizationLogoUrl,
          })).id;
        orgIdCache.set(key, id);
        return id;
      };

      // Resolve a pick to its TenantBrand id, creating its parent chain
      // first so `parentBrandId` links the hierarchy. `visiting` guards
      // against a cyclic parentBrandName reference.
      const resolveBrand = async (
        p: FlatPick,
        visiting: Set<FlatPick>,
      ): Promise<number> => {
        const cached = brandIdCache.get(p);
        if (cached != null) return cached;
        if (p.brand_id != null) {
          brandIdCache.set(p, p.brand_id);
          return p.brand_id;
        }
        const orgId = await resolveOrg(p);
        let parentBrandId: number | null = null;
        if (p.parentBrandName) {
          const parentPick = pickByOrgBrand.get(`${orgKeyOf(p)}|${norm(p.parentBrandName)}`);
          if (parentPick && parentPick !== p && !visiting.has(parentPick)) {
            parentBrandId = await resolveBrand(parentPick, new Set(visiting).add(p));
          } else if (!parentPick) {
            // Parent named but not returned as its own row — create a
            // bare brand under the org so the child can link to it.
            parentBrandId = (
              await findOrCreateTenantBrand(p.parentBrandName.trim(), orgId, tenantId)
            ).id;
          }
        }
        const created = await findOrCreateTenantBrand(
          p.brand.trim(),
          orgId,
          tenantId,
          {
            description: p.brandDescription,
            website: p.brandWebSiteUrl,
            logoUrl: p.brandLogoUrl,
          },
          parentBrandId,
        );
        brandIdCache.set(p, created.id);
        return created.id;
      };

      for (const pick of chosen) {
        if (!pick.brand.trim()) continue;
        const brandId = await resolveBrand(pick, new Set());
        await resources.lineOfBizBrandsFor(lineOfBizId).update(brandId, {
          associationType: pick.associationType,
          associationDescription: pick.associationDescription ?? '',
        });
      }
    },
    onSuccess: () => {
      // Refresh both the junction list (this page) and the tenant
      // brand / org caches so the TenantBrandTreeSingleSelect picker
      // sees the new rows.
      queryClient.invalidateQueries({ queryKey: ['line-of-biz-brands'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-brands'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-organizations'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const retry = () => {
    if (lineOfBizId == null) return;
    setStatus(null);
    setError(null);
    setStartError(null);
    setSelected(new Set());
    jobIdRef.current = null;
    start.mutate(lineOfBizId);
  };

  // ----- open / reset / start -----------------------------------------
  useEffect(() => {
    if (!open) return;
    setStatus(null);
    setError(null);
    setStartError(null);
    setSelected(new Set());
    jobIdRef.current = null;
    if (lineOfBizId != null) start.mutate(lineOfBizId);
    // start.mutate identity is stable for the mutation hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lineOfBizId]);

  // ----- polling loop -------------------------------------------------
  useEffect(() => {
    if (!open) return;
    if (status == null) return;
    if (TERMINAL_STATES.has(status.state)) return;
    let cancelled = false;
    const tick = async () => {
      const jobId = jobIdRef.current;
      if (!jobId) return;
      try {
        const next = await llmAgent.getJob<CompetitorsResult>(jobId);
        if (!cancelled) setStatus(next);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      }
    };
    const handle = window.setInterval(tick, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [open, status]);

  // Flatten the model's nested orgs → brands shape into one row per
  // suggested brand. Memoised so selection-state indexes stay stable
  // across re-renders.
  const picks = useMemo<FlatPick[]>(
    () => flattenSuggestions(status?.result),
    [status?.result],
  );

  // Default selection — once a job completes, preselect the focus-level
  // brands (the actual competitors). Ancestor brands returned only to
  // complete the hierarchy are left unticked: they're still created as
  // TenantBrand rows when a selected child needs them, but we don't
  // attach a LOB junction for them unless the user opts in.
  useEffect(() => {
    if (status?.state !== 'completed') return;
    setSelected(
      new Set(picks.map((p, idx) => (p.isFocusLevel ? idx : -1)).filter((i) => i >= 0)),
    );
  }, [status, picks]);

  const lastStep = status?.steps?.[status.steps.length - 1]?.step;

  const toggle = (idx: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  const isCompleted = status?.state === 'completed';
  const isRunning =
    start.isPending || (status != null && !TERMINAL_STATES.has(status.state));
  const canSave = isCompleted && selected.size > 0 && !save.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setError(null);
    const chosen = picks.filter((_, idx) => selected.has(idx));
    await save.mutateAsync({ chosen, all: picks });
  };

  return (
    <FormDialog
      open={open}
      title="Generate brands"
      submitLabel={save.isPending ? 'Saving…' : 'Save selected'}
      loading={save.isPending}
      submitDisabled={!canSave}
      maxWidth="lg"
      onSubmit={handleSubmit}
      onClose={() => {
        if (save.isPending) return;
        onClose();
      }}
    >
      {error && <Alert severity="error">{error}</Alert>}

      {/* ---- Job progress ---- */}
      {isRunning && (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              {status?.state === 'pending' || status == null
                ? 'Starting…'
                : lastStep ?? 'Researching competitor brands…'}
            </Typography>
          </Stack>
          <LinearProgress
            variant={
              status?.progress != null && status.progress > 0
                ? 'determinate'
                : 'indeterminate'
            }
            value={
              status?.progress != null
                ? Math.round(status.progress * 100)
                : undefined
            }
          />
        </Stack>
      )}

      {status?.state === 'failed' && (
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          {status.error || 'The competitor-research job failed.'}
        </Alert>
      )}
      {status?.state === 'cancelled' && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          The competitor-research job was cancelled.
        </Alert>
      )}
      {startError && (
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          {startError}
        </Alert>
      )}

      {/* ---- Results review ---- */}
      {isCompleted && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Focus-level banner — the brand level the model matched the
              competitors to (e.g. product/model vs. top-level brand).
              Helps the user confirm the analysis targeted the right tier. */}
          {status?.result?.requested_brand_level && picks.length > 0 && (
            <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
              <Typography variant="body2">
                Competitors matched at level:{' '}
                <strong>{status.result.requested_brand_level}</strong>
              </Typography>
            </Alert>
          )}
          {picks.length === 0 ? (
            <Alert severity="warning">
              The analyzer returned no brands.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {picks.map((c, idx) => (
                <Paper
                  key={`${c.brand}-${idx}`}
                  variant="outlined"
                  // Indent by hierarchy depth so child brands (e.g. models
                  // under a brand) read as nested under their parent.
                  sx={{ p: 1.5, ml: c.depth * 3 }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Checkbox
                      checked={selected.has(idx)}
                      onChange={() => toggle(idx)}
                      sx={{ p: 0.5, mt: -0.25 }}
                    />
                    {/* Brand logo (if the model returned a URL). We render
                        it as an Avatar so missing/broken URLs degrade
                        gracefully to the placeholder. */}
                    <Avatar
                      variant="rounded"
                      src={c.brandLogoUrl || undefined}
                      alt={c.brand}
                      sx={{ width: 40, height: 40, bgcolor: 'action.selected' }}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      {c.organization && (
                        <Typography variant="caption" color="text.secondary">
                          {c.organization}
                          {c.organization_id != null && ' · existing'}
                          {c.parentBrandName && ` · under ${c.parentBrandName}`}
                        </Typography>
                      )}
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ flexWrap: 'wrap' }}
                      >
                        <Typography variant="subtitle1" fontWeight={700}>
                          {c.brand}
                        </Typography>
                        {/* Association-type chip — the model picks one of
                            OWNER / COMPETITOR / SUBSIDIARY / PARTNER per
                            brand; the chip shows it so the user can scan
                            owners vs competitors at a glance. */}
                        <Chip
                          size="small"
                          label={c.associationType}
                          color={c.associationType === 'OWNER' ? 'primary' : 'default'}
                          variant={c.associationType === 'OWNER' ? 'filled' : 'outlined'}
                        />
                        {c.brand_id != null && (
                          <Chip size="small" label="existing" variant="outlined" />
                        )}
                        {/* Ancestor brand returned only to complete the
                            hierarchy — not a focus-level competitor. */}
                        {!c.isFocusLevel && (
                          <Chip
                            size="small"
                            label="hierarchy"
                            variant="outlined"
                            color="info"
                          />
                        )}
                      </Stack>

                      {/* Brand description / website. Website doubles as
                          a sanity check — the user can click through to
                          confirm the model didn't hallucinate. */}
                      {c.brandDescription && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {c.brandDescription}
                        </Typography>
                      )}
                      {c.brandWebSiteUrl && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                          <a
                            href={c.brandWebSiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit' }}
                          >
                            {c.brandWebSiteUrl}
                          </a>
                        </Typography>
                      )}

                      {/* Association rationale — why the model assigned
                          this association type. Stored on the junction's
                          `associationDescription`. */}
                      {c.associationDescription && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Why
                          </Typography>
                          <Typography variant="body2">
                            {c.associationDescription}
                          </Typography>
                        </Box>
                      )}

                      {/* Organization-level metadata — collapsed under a
                          small caption so the row stays scannable when
                          the model returns a lot of context. */}
                      {(c.organizationDescription
                        || c.organizationWebSiteUrl) && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            About {c.organization || 'organization'}
                          </Typography>
                          {c.organizationDescription && (
                            <Typography variant="body2">
                              {c.organizationDescription}
                            </Typography>
                          )}
                          {c.organizationWebSiteUrl && (
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              <a
                                href={c.organizationWebSiteUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'inherit' }}
                              >
                                {c.organizationWebSiteUrl}
                              </a>
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------
// Helpers — find-or-create by name. Both endpoints accept `filter[name]`
// against an exact string match (case-sensitive on the DB side), so we
// first list with the trimmed name and fall back to create when empty.
// We don't try to dedupe by alias — that's left to the existing brands
// management UI; if the model spits a brand we don't already have, we'd
// rather create a fresh row than guess at a fuzzy match.
// ---------------------------------------------------------------------------

interface BrandOrgExtras {
  description?: string;
  website?: string;
  logoUrl?: string;
}

/**
 * `description` / `website` / `logoUrl` are only sent on CREATE — we
 * never overwrite a row the user has already curated. `clean()` drops
 * empty strings so a partial LLM response doesn't blank out optional
 * fields client-side via JSON coercion.
 */
function clean(extras: BrandOrgExtras): Partial<BrandOrgExtras> {
  const out: Partial<BrandOrgExtras> = {};
  if (extras.description?.trim()) out.description = extras.description.trim();
  if (extras.website?.trim()) out.website = extras.website.trim();
  if (extras.logoUrl?.trim()) out.logoUrl = extras.logoUrl.trim();
  return out;
}

async function findOrCreateTenantOrganization(
  name: string,
  tenantId: number,
  extras: BrandOrgExtras = {},
): Promise<TenantOrganization> {
  const list = await resources.tenantOrganizations.list({
    pageSize: 25,
    filter: { name, tenantId: tenantId },
  });
  const exact = list.data.find(
    (o) => o.name === name && o.tenantId === tenantId,
  );
  if (exact) return exact;
  return resources.tenantOrganizations.create({
    name,
    tenantId: tenantId,
    ...clean(extras),
  } as Partial<TenantOrganization>);
}

async function findOrCreateTenantBrand(
  name: string,
  organizationId: number,
  tenantId: number,
  extras: BrandOrgExtras = {},
  parentBrandId: number | null = null,
): Promise<TenantBrand> {
  const list = await resources.tenantBrands.list({
    pageSize: 25,
    filter: { name, organizationId, tenantId: tenantId },
  });
  const exact = list.data.find(
    (b) =>
      b.name === name
      && b.organizationId === organizationId
      && b.tenantId === tenantId,
  );
  // Reuse a curated row as-is — we never re-parent an existing brand.
  if (exact) return exact;
  return resources.tenantBrands.create({
    name,
    organizationId,
    tenantId: tenantId,
    ...(parentBrandId != null ? { parentBrandId } : {}),
    ...clean(extras),
  } as Partial<TenantBrand>);
}
